import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Wordmark } from "@/components/Wordmark";
import {
  Activity,
  ClipboardList,
  Sparkles,
  Users,
  Check,
  ArrowRight,
  Receipt,
  BarChart3,
  CloudSun,
  Package,
  CalendarClock,
  UsersRound,
  Library,
} from "lucide-react";
import { CreditFlowAnimation } from "@/components/packages/CreditFlowAnimation";
import { InvoicePaidAnimation } from "@/components/landing/InvoicePaidAnimation";
import heroCoachAsset from "@/assets/landing/hero-coach.webp.asset.json";
import featureLogAsset from "@/assets/landing/feature-log.webp.asset.json";
import featureRecapAsset from "@/assets/landing/feature-recap.webp.asset.json";
import featureClientsAsset from "@/assets/landing/feature-clients.webp.asset.json";
import sportCollageAsset from "@/assets/landing/sport-collage.webp.asset.json";
import featureBookingAsset from "@/assets/landing/feature-booking.jpg.asset.json";
import featureClinicsAsset from "@/assets/landing/feature-clinics.jpg.asset.json";
import featureDrillsAsset from "@/assets/landing/feature-drills.jpg.asset.json";

const heroCoach = heroCoachAsset.url;
const featureLog = featureLogAsset.url;
const featureRecap = featureRecapAsset.url;
const featureClients = featureClientsAsset.url;
const sportCollage = sportCollageAsset.url;
import pickleballImg from "@/assets/landing/sports/pickleball.jpg.asset.json";
import tennisImg from "@/assets/landing/sports/tennis.jpg.asset.json";
import golfImg from "@/assets/landing/sports/golf.jpg.asset.json";
import soccerImg from "@/assets/landing/sports/soccer.jpg.asset.json";
import baseballImg from "@/assets/landing/sports/baseball.jpg.asset.json";
import weightliftingImg from "@/assets/landing/sports/weightlifting.jpg.asset.json";
import wetCourtImg from "@/assets/landing/wet-court.jpg.asset.json";
import { MarketingFooter } from "@/components/MarketingFooter";
import { buildSocialMeta, SITE_URL } from "@/lib/seo";
import { SPORTS } from "@/lib/starter-sports";

const SPORT_IMAGES: Record<string, string> = {
  pickleball: pickleballImg.url,
  tennis: tennisImg.url,
  golf: golfImg.url,
  soccer: soccerImg.url,
  baseball: baseballImg.url,
  weightlifting: weightliftingImg.url,
};

export const Route = createFileRoute("/")({
  head: () => ({
    meta: buildSocialMeta({
      title: "CoachQuick: Run Your Coaching Business in One Place",
      description:
        "Coaching, minus the busy work: log lessons, send automated recaps, manage drills, run clinics, book lessons, and invoice clients- for all sport coaches.",
      path: "/",
    }),
    links: [
      { rel: "canonical", href: `${SITE_URL}/` },
      { rel: "preload", as: "image", href: heroCoachAsset.url, fetchpriority: "high" },
    ],
  }),
  component: Landing,
});

function Landing() {
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero with full-bleed background */}
      <section className="relative min-h-[100vh] w-full overflow-hidden bg-[#0a0a0a] text-white">
        <img
          src={heroCoach}
          alt="Coach mentoring young athletes on a tennis court"
          width={2400}
          height={1600}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 35%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0.9) 100%)",
          }}
        />

        {/* Nav */}
        <header className="relative z-20">
          <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
            <Link to="/" className="flex items-center" aria-label="CoachQuick home">
              <Wordmark className="text-2xl [&_span]:!text-white" />
            </Link>
            <nav className="hidden items-center gap-10 text-sm font-medium text-white/85 md:flex">
              <a href="#features" className="transition hover:text-white">
                Features
              </a>
              <a href="#how" className="transition hover:text-white">
                How it works
              </a>
              <a href="#sports" className="transition hover:text-white">
                Sports
              </a>
              <a href="#pricing" className="transition hover:text-white">
                Pricing
              </a>
            </nav>
            <div className="flex items-center gap-3">
              <Link to="/auth" className="hidden text-sm font-medium text-white/85 hover:text-white md:inline-flex">
                Sign in
              </Link>
              <Link to="/auth" search={{ mode: "signup" } as never}>
                <Button className="rounded-full bg-primary px-6 font-semibold text-primary-foreground shadow-lg hover:bg-primary/90">
                  Start Free
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero copy */}
        <div className="relative z-10 mx-auto flex max-w-7xl flex-col items-center px-6 pt-12 pb-10 text-center md:pt-20">
          <h1 className="mt-8 font-display text-5xl font-light leading-[1.05] tracking-tight text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)] md:text-7xl lg:text-8xl">
            Run your coaching
            <br />
            <span className="font-semibold">business in one place.</span>
          </h1>
          <p
            className="mt-6 max-w-2xl text-base text-white/90 md:text-lg"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.6)" }}
          >
            Log lessons, send automated recaps, invoice clients, and re-engage. <br />
            Customer management for sport coaches.
          </p>
          <div className="mt-10">
            <Link to="/auth" search={{ mode: "signup" } as never}>
              <Button
                size="lg"
                className="h-14 rounded-full bg-primary px-10 text-base font-semibold text-primary-foreground shadow-2xl hover:bg-primary/90"
              >
                Start Coaching Smarter
              </Button>
            </Link>
          </div>
        </div>

        {/* Floating feature cards over hero */}
        <div className="relative z-10 mx-auto max-w-7xl px-6 pb-20 md:pb-24">
          <div className="grid gap-5 md:grid-cols-3">
            {[
              {
                icon: Users,
                title: "Client Profiles & History",
                desc: "Keep tabs on your clients and their progressions.",
              },
              {
                icon: ClipboardList,
                title: "Court-side Session Logger",
                desc: "Pick a client, tap techniques, drills and recommendations as you go. Done in 30 seconds.",
              },
              {
                icon: Sparkles,
                title: "Automated Lesson Recaps",
                desc: "One-click warm summary email with the drills you covered and clear homework.",
              },
            ].map((f) => (
              <div
                key={f.title}
                className="rounded-2xl border border-white/15 bg-black/40 p-6 backdrop-blur-md transition hover:bg-black/55"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-brand-text ring-1 ring-primary/30">
                  <f.icon className="h-5 w-5" />
                </div>
                <h2 className="mt-5 text-lg font-semibold text-white">{f.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-white/75">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-border bg-secondary/30 py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
              More clients on the roster <br />
              Less time on the clipboard
            </h2>
            <p className="mt-4 text-muted-foreground">
              CoachQuick handles the follow-ups, reminders, and check-ins that turn one-time clients into regulars... so
              you can focus on coaching.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Users,
                title: "Client profiles & history",
                desc: "Keep tabs on your clients and their progressions.",
                image: featureClients,
                imageAlt: "Coach addressing a group of young athletes kneeling on a turf field",
              },
              {
                icon: ClipboardList,
                title: "Court-side logger & automated recaps",
                desc: "Tap drills and techniques as you coach. One click sends a warm summary email with homework — done in 30 seconds.",
                image: featureLog,
                imageAlt: "Overhead view of a tennis player on court surrounded by scattered balls",
              },
              {
                icon: CalendarClock,
                title: "Booking page that fills your day",
                desc: "Share one link instead of Calendly. Clients pick from real openings, and cascading hours pull each booking up against the last so your day fills front-to-back with no dead gaps.",
                image: featureBookingAsset.url,
                imageAlt: "Phone held court-side showing a week of bookable lesson time slots",
              },
              {
                icon: Package,
                title: "Lesson packages & prepaid bundles",
                desc: "Sell 5, 10, or 20-lesson packs. Clients pay upfront, credits tick down as you log sessions, and your cancellation policy handles no-shows automatically.",
                visual: (
                  <div className="aspect-[4/3] w-full bg-secondary/40 flex items-center justify-center p-4">
                    <CreditFlowAnimation />
                  </div>
                ),
              },
              {
                icon: UsersRound,
                title: "Clinics & group events",
                desc: "Create a clinic, set capacity, and share the registration link. Players sign up and pay online, waitlists fill cancellations, and you can message the whole roster at once.",
                image: featureClinicsAsset.url,
                imageAlt: "Group of players lined up on a pickleball court facing their coach",
              },
              {
                icon: BarChart3,
                title: "Re-engagement & marketing",
                desc: "Quietly win back fading clients and turn the rest into referrals - with smart nudges and an automated newsletter that practically sells itself.",
                image: featureRecap,
                imageAlt: "Coach reviewing client emails on a laptop",
              },
              {
                icon: CloudSun,
                title: "Weather heads-up",
                desc: "10-day outdoor forecasts and severe-weather alerts for every court so you can reschedule early and avoid surprise cancellations.",
                image: wetCourtImg.url,
                imageAlt: "Rain-soaked pickleball court with net",
              },
              {
                icon: Library,
                title: "Drill & technique library",
                desc: "Start with a library tuned to your sport, edit it to match how you teach, and attach video clips so clients can rewatch the exact drill after the lesson.",
                image: featureDrillsAsset.url,
                imageAlt: "Cones, target discs, and a ball hopper set up on an empty court at sunrise",
              },
              {
                icon: Receipt,
                title: "Invoicing & card payments",
                desc: "Send branded invoices for one-off lessons, collect cards via Stripe, and track paid / overdue at a glance.",
                visual: (
                  <div className="aspect-[4/3] w-full bg-secondary/40 flex items-center justify-center p-4">
                    <InvoicePaidAnimation />
                  </div>
                ),
              },
            ].map((f) => (
              <div key={f.title} className="overflow-hidden rounded-xl border border-border bg-card shadow-soft">
                {f.visual ? (
                  f.visual
                ) : f.image ? (
                  <img
                    src={f.image}
                    alt={f.imageAlt ?? ""}
                    loading="lazy"
                    decoding="async"
                    width={800}
                    height={600}
                    className="aspect-[4/3] w-full object-cover"
                  />
                ) : null}
                <div className="p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-semibold">{f.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">How it works</h2>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[
              {
                n: "01",
                t: "Add clients & your library",
                d: "Bring in your athletes and start with our pickleball-tuned drill set — edit anything to match how you teach.",
              },
              {
                n: "02",
                t: "Log sessions court-side",
                d: "From your phone, pick a client and tap drills as you cover them. Add a quick note. Done.",
              },
              {
                n: "03",
                t: "Recap, invoice, re-engage",
                d: "Send the automated recap with homework, attach an invoice, and let CoachQuick nudge clients who go quiet.",
              },
            ].map((s) => (
              <div key={s.n} className="relative">
                <div className="text-5xl font-bold text-primary/20" aria-hidden="true">
                  {s.n}
                </div>
                <h3 className="mt-2 text-lg font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sports */}
      <section id="sports" className="overflow-hidden border-t border-border py-20">
        <style>{`
          @keyframes cq-scroll-x {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .cq-marquee-track {
            animation: cq-scroll-x 40s linear infinite;
            will-change: transform;
          }
          
          @media (prefers-reduced-motion: reduce) {
            .cq-marquee-track { animation: none; }
          }
        `}</style>
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Built for your sport</h2>
            <p className="mt-4 text-muted-foreground">
              Pickleball, tennis, golf, soccer, baseball, and weightlifting — each with a starter library of techniques,
              drills, and resources you can edit.
            </p>
          </div>
        </div>
        <div
          className="mt-12 w-full"
          style={{
            maskImage: "linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)",
          }}
        >
          <div className="cq-marquee-track flex w-max gap-5">
            {[...SPORTS, ...SPORTS].map((sport, i) => (
              <div
                key={`${sport.id}-${i}`}
                className="group relative h-44 w-64 shrink-0 overflow-hidden rounded-xl border border-border bg-card shadow-soft sm:h-52 sm:w-72"
              >
                <img
                  src={SPORT_IMAGES[sport.id] ?? sportCollage}
                  alt={`${sport.label} coaching`}
                  loading="lazy"
                  decoding="async"
                  width={288}
                  height={208}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent p-4">
                  <div className="flex items-center gap-2 text-white">
                    <sport.icon className="h-4 w-4" />
                    <span className="text-sm font-semibold tracking-tight">{sport.label}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}

      <section className="bg-card py-16">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="text-2xl font-medium leading-relaxed md:text-3xl">
            "I cut my admin time in half. Recap emails that used to take 20 minutes now take two."
          </p>
          <p className="mt-4 text-sm text-muted-foreground">— Independent pickleball coach, beta user</p>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-border py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Simple pricing. Cancel anytime.</h2>
            <p className="mt-4 text-muted-foreground">
              Every plan starts with a free 30-day trial of everything — no credit card, and your data is always yours
              to export.
            </p>
          </div>

          {/* Billing toggle */}
          <div className="mt-8 flex justify-center">
            <div className="inline-flex items-center rounded-full border border-border bg-card p-1 shadow-sm">
              <button
                type="button"
                onClick={() => setIsAnnual(false)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                  !isAnnual ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setIsAnnual(true)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                  isAnnual ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Annual
              </button>
            </div>
          </div>

          {/* Cards */}
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              {
                name: "Starter",
                tagline: "For coaches getting started.",
                monthlyPrice: "$0",
                annualPrice: "$0",
                annualBilling: null,
                bullets: [
                  "Unlimited clients · 5 visible at a time",
                  "3 AI-written client emails per week",
                  "Booking page for one session type",
                  "Client notes & history",
                ],
                cta: "Start free",
                variant: "outline" as const,
                popular: false,
                footnote: null,
              },
              {
                name: "Pro",
                tagline: "For working coaches.",
                monthlyPrice: "$12",
                annualPrice: "$10",
                annualBilling: "$120/yr (2 months free)",
                bullets: [
                  "Unlimited clients",
                  "Unlimited AI-written client emails",
                  "Full scheduling with cascading hours — your day fills front-to-back, no gaps",
                  "Invoices & automatic reminders",
                  "Everything in Starter",
                ],
                cta: "Start 30-day trial",
                variant: "default" as const,
                popular: true,
                footnote: null,
              },
              {
                name: "Clinics",
                tagline: "For coaches who run group events.",
                monthlyPrice: "$19",
                annualPrice: "$15.83",
                annualBilling: "$190/yr (2 months free)",
                bullets: [
                  "Create & sell clinics",
                  "Registration, capacity limits & waitlists",
                  "Group payments collected for you",
                  "Everything in Pro",
                ],
                cta: "Start 30-day trial",
                variant: "default" as const,
                popular: false,
                footnote: "One 10-player clinic pays for a full year.",
              },
            ].map((plan) => (
              <div
                key={plan.name}
                className={`relative flex flex-col rounded-2xl border bg-card p-6 shadow-soft ${
                  plan.popular ? "border-2 border-primary" : "border-border"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                    Most Popular
                  </div>
                )}
                <div className="mb-4">
                  <h3 className="text-xl font-bold">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground">{plan.tagline}</p>
                </div>
                <div className="mb-5">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold">{isAnnual ? plan.annualPrice : plan.monthlyPrice}</span>
                    <span className="text-sm text-muted-foreground">/mo</span>
                  </div>
                  {isAnnual && plan.annualBilling && (
                    <p className="mt-1 text-sm text-muted-foreground">Billed annually at {plan.annualBilling}</p>
                  )}
                  {!isAnnual && plan.monthlyPrice !== "$0" && (
                    <p className="mt-1 text-sm text-muted-foreground">or {plan.annualBilling} when billed annually</p>
                  )}
                </div>
                <ul className="mb-6 flex-1 space-y-3">
                  {plan.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-text" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                {plan.footnote && <p className="mb-4 text-sm italic text-muted-foreground">{plan.footnote}</p>}
                <Link to="/auth" search={{ mode: "signup" } as never} className="mt-auto">
                  <Button variant={plan.variant} className="w-full">
                    {plan.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          <p className="mx-auto mt-12 max-w-3xl text-center text-sm text-muted-foreground">
            Every new account gets 30 days of Clinics-tier access free. When your trial ends, you keep your account,
            your clients, and your data — you just drop to Starter until you&apos;re ready. Export everything, anytime.
          </p>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
